import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { InlineSwitcher } from "components/common/InlineSwitcher";
import { CenteredLoader } from "../../CenteredLoader";
import { CenteredNotice } from "../CenteredNotice";
import { usePlanComparisonData } from "../hooks/usePlanComparisonData";
import { resolveTrayScaleNotice } from "../utils/resolveTrayScaleNotice";
import { QuantityDiffSection } from "./QuantityDiffSection";
import { QuantityDiffSummary } from "./QuantityDiffSummary";
import { QuantityStatusFilter } from "./QuantityStatusFilter";
import { TrayScaleNotice } from "./TrayScaleNotice";
import {
  COLUMN_HEADER_STICKY_CLASS,
  KIND_LABEL_KEY,
  QUANTITY_GRID_CLASS,
} from "./constants";
import type {
  QuantityDiffEntry,
  QuantityDiffStatus,
} from "../utils/computeQuantityDiff";
import type { QuantityKind } from "../utils/formatQuantityValue";
import type { QuantityStatusFilterValue } from "./QuantityStatusFilter";
import type { QuantitiesTabProps } from "./types";

type EntriesByStatus = Record<QuantityDiffStatus, QuantityDiffEntry[]>;

const buildEmptyEntriesByStatus = (): EntriesByStatus => ({
  added: [],
  removed: [],
  changed: [],
  unchanged: [],
});

export const QuantitiesTab = ({
  beforePlanRecord,
  afterPlanRecord,
}: QuantitiesTabProps) => {
  const { t } = useTranslation();

  const listScrollRef = useRef<HTMLDivElement>(null);

  const [activeKind, setActiveKind] = useState<QuantityKind>("elements");
  const [activeStatusFilter, setActiveStatusFilter] =
    useState<QuantityStatusFilterValue>("all");

  // A status can be empty for the other kind, which would leave an empty list.
  const handleKindChange = useCallback(
    (kind: QuantityKind) => {
      if (kind === activeKind) return;
      setActiveKind(kind);
      setActiveStatusFilter("all");
      listScrollRef.current?.scrollTo({ top: 0 });
    },
    [activeKind],
  );

  const handleStatusFilterChange = useCallback(
    (value: QuantityStatusFilterValue) => {
      if (value === activeStatusFilter) return;
      setActiveStatusFilter(value);
      listScrollRef.current?.scrollTo({ top: 0 });
    },
    [activeStatusFilter],
  );

  const { beforePlan, afterPlan, quantityDiff, isLoading, isError } =
    usePlanComparisonData({
      beforePlanRecord,
      afterPlanRecord,
    });

  const trayScaleNotice = useMemo(
    () => resolveTrayScaleNotice({ beforePlan, afterPlan }),
    [beforePlan, afterPlan],
  );

  const activeSet = quantityDiff === null ? null : quantityDiff[activeKind];

  const entriesByStatus = useMemo(() => {
    const grouped = buildEmptyEntriesByStatus();
    for (const entry of activeSet?.entries ?? []) {
      grouped[entry.status].push(entry);
    }
    return grouped;
  }, [activeSet]);

  const entryCountByStatus = useMemo(
    () => ({
      added: entriesByStatus.added.length,
      removed: entriesByStatus.removed.length,
      changed: entriesByStatus.changed.length,
      unchanged: entriesByStatus.unchanged.length,
    }),
    [entriesByStatus],
  );

  const isStatusVisible = (status: QuantityDiffStatus) =>
    activeStatusFilter === "all" || activeStatusFilter === status;

  if (isLoading) return <CenteredLoader />;

  if (isError || quantityDiff === null || activeSet === null) {
    return (
      <CenteredNotice
        message={t("smartSurveyor.planComparison.quantities.loadFailed")}
      />
    );
  }

  const hasDifferences = activeSet.totals.changedEntryCount > 0;

  return (
    <div className="mx-auto flex min-h-0 w-full max-w-5xl flex-1 flex-col gap-4">
      <QuantityDiffSummary
        elements={quantityDiff.elements}
        cables={quantityDiff.cables}
        activeKind={activeKind}
        onKindSelect={handleKindChange}
      />
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <InlineSwitcher<QuantityKind>
          value={activeKind}
          onChange={handleKindChange}
          variant="segmented"
          className="w-full max-w-xs lg:w-56 lg:max-w-none"
          listClassName="text-sm"
          options={[
            {
              value: "elements",
              label: t(KIND_LABEL_KEY.elements),
            },
            {
              value: "cables",
              label: t(KIND_LABEL_KEY.cables),
            },
          ]}
        />
        <QuantityStatusFilter
          entryCountByStatus={entryCountByStatus}
          totalEntryCount={activeSet.entries.length}
          kind={activeKind}
          value={activeStatusFilter}
          onChange={handleStatusFilterChange}
        />
      </div>
      {activeKind === "cables" &&
        trayScaleNotice !== null &&
        activeSet.entries.length > 0 && (
          <TrayScaleNotice notice={trayScaleNotice} />
        )}
      {/* Inside the scroller so the columns stay aligned when a scrollbar takes width. */}
      <div ref={listScrollRef} className="min-h-0 flex-1 overflow-y-auto">
        <div
          className={`${QUANTITY_GRID_CLASS} ${COLUMN_HEADER_STICKY_CLASS} border-b border-gray-100 px-2 text-xs font-medium text-gray-500 sm:px-3`}
        >
          <span>{t("smartSurveyor.planComparison.quantities.type")}</span>
          <span className="text-right" title={beforePlanRecord.name}>
            {t("smartSurveyor.planComparison.quantities.before")}
          </span>
          <span className="text-right" title={afterPlanRecord.name}>
            {t("smartSurveyor.planComparison.quantities.after")}
          </span>
          <span className="text-right">
            {t("smartSurveyor.planComparison.quantities.change")}
          </span>
        </div>
        {!hasDifferences && (
          <div className="mb-4 mt-2 flex flex-col items-center gap-2 py-8">
            <div className="rounded-full bg-green-50 p-3 text-green-600">
              <Check size={24} />
            </div>
            <p className="text-sm font-medium text-gray-700">
              {t("smartSurveyor.planComparison.quantities.identical")}
            </p>
            <p className="text-xs text-gray-500">
              {t("smartSurveyor.planComparison.quantities.identicalHint", {
                kind: t(KIND_LABEL_KEY[activeKind]),
              })}
            </p>
          </div>
        )}
        {isStatusVisible("added") && (
          <QuantityDiffSection
            title={t("smartSurveyor.planComparison.quantities.added")}
            status="added"
            entries={entriesByStatus.added}
            kind={activeKind}
          />
        )}
        {isStatusVisible("removed") && (
          <QuantityDiffSection
            title={t("smartSurveyor.planComparison.quantities.removed")}
            status="removed"
            entries={entriesByStatus.removed}
            kind={activeKind}
          />
        )}
        {isStatusVisible("changed") && (
          <QuantityDiffSection
            title={t("smartSurveyor.planComparison.quantities.changed")}
            status="changed"
            entries={entriesByStatus.changed}
            kind={activeKind}
          />
        )}
        {isStatusVisible("unchanged") && (
          <QuantityDiffSection
            title={t("smartSurveyor.planComparison.quantities.unchanged")}
            status="unchanged"
            entries={entriesByStatus.unchanged}
            kind={activeKind}
            isCollapsible={activeStatusFilter === "all"}
          />
        )}
      </div>
    </div>
  );
};
